import { RANK_SUBRANKS } from "@/features/matches";

import type { PlayerSummary } from "./PlayerSummary";

type RankPoint = PlayerSummary["rank_history"][number];

export type RankPathPoint = RankPoint & {
  // Положение на графике в долях: `x` — слева направо по матчам, `y` — сверху вниз.
  x: number;
  y: number;
};

export type RankPathLevel = {
  badge: number;
  y: number;
  // Первый подуровень тира: линия сетки ярче.
  tierStart: boolean;
};

export type RankPathTier = {
  tier: number;
  // Середина подуровней тира, попавших на график.
  y: number;
  subranks: [number, number];
};

export type RankPath = {
  points: RankPathPoint[];
  levels: RankPathLevel[];
  tiers: RankPathTier[];
  // Следующий подуровень после последнего матча — пунктир «цель»; выше Eternus 6 — `null`.
  goal: { badge: number; y: number } | null;
  // Линия через точки в координатах 0..100 для `<path d>`: ровная, пока ранг тот же,
  // переход между подуровнями — плавной S-кривой без выбросов за соседние точки.
  linePath: string;
};

// Самый низкий и самый высокий ранг: Initiate 1 и Eternus 6 (у Obscurus подуровней нет).
const MIN_BADGE = 11;
const MAX_BADGE = 116;
// Сколько подуровней график показывает самое меньшее, чтобы ровный ранг не лёг на край.
const MIN_LEVELS = 4;

const ordinal = (badge: number): number =>
  Math.floor(badge / 10) * RANK_SUBRANKS + (badge % 10) - 1;
const badgeOf = (level: number): number =>
  Math.floor(level / RANK_SUBRANKS) * 10 + (level % RANK_SUBRANKS) + 1;

// «Путь по рангам»: ранг после каждого рейтингового матча от старого к новому.
// По вертикали — подуровни от самого низкого ранга до следующего после последнего матча.
export const rankPath = (history: readonly RankPoint[]): RankPath | null => {
  const last = history.at(-1);
  if (last === undefined) {
    return null;
  }
  const goal = last.rank_badge < MAX_BADGE ? badgeOf(ordinal(last.rank_badge) + 1) : null;
  const ordinals = history.map((point) => ordinal(point.rank_badge));
  const high = Math.max(...ordinals, goal === null ? 0 : ordinal(goal));
  const low = Math.max(ordinal(MIN_BADGE), Math.min(...ordinals, high - (MIN_LEVELS - 1)));
  const y = (level: number) => (high === low ? 0.5 : (high - level) / (high - low));
  const x = (index: number) => (history.length === 1 ? 0.5 : index / (history.length - 1));

  const points = history.map((point, index) => ({
    ...point,
    x: x(index),
    y: y(ordinal(point.rank_badge)),
  }));
  const levels: RankPathLevel[] = [];
  const tiers = new Map<number, number[]>();
  for (let level = low; level <= high; level += 1) {
    const badge = badgeOf(level);
    levels.push({ badge, y: y(level), tierStart: badge % 10 === 1 });
    const tier = Math.floor(badge / 10);
    tiers.set(tier, [...(tiers.get(tier) ?? []), level]);
  }
  const coord = (value: number) => (value * 100).toFixed(2);
  // Кубическая кривая с опорами на середине отрезка по горизонтали: касательные в точках
  // горизонтальные, поэтому линия не уходит выше и ниже соседних рангов.
  const linePath = points
    .map((point, index) => {
      const previous = points[index - 1];
      if (previous === undefined) {
        return `M ${coord(point.x)} ${coord(point.y)}`;
      }
      const middle = coord((previous.x + point.x) / 2);
      return `C ${middle} ${coord(previous.y)} ${middle} ${coord(point.y)} ${coord(point.x)} ${coord(point.y)}`;
    })
    .join(" ");

  return {
    points,
    levels,
    tiers: [...tiers].map(([tier, tierLevels]) => {
      const first = tierLevels[0] ?? low;
      const lastLevel = tierLevels.at(-1) ?? first;
      return {
        tier,
        y: (y(first) + y(lastLevel)) / 2,
        subranks: [badgeOf(first) % 10, badgeOf(lastLevel) % 10],
      };
    }),
    goal: goal === null ? null : { badge: goal, y: y(ordinal(goal)) },
    linePath,
  };
};
